import React, { useEffect, useState } from 'react';
import {
  AlertCircle, Check, CheckCircle2, Copy, ExternalLink, Loader2, Lock, Mail, MessageCircle, MessageSquareText, Send, Star, Zap,
} from 'lucide-react';
import styles from './InvoiceActionPanel.module.css';

/* Renders WhatsApp *bold* inside the preview */
const WaText = ({ text }) =>
  String(text || '')
    .split(/(\*[^*\n]+\*)/g)
    .map((part, i) =>
      part.startsWith('*') && part.endsWith('*') && part.length > 2 ? <b key={i}>{part.slice(1, -1)}</b> : part
    );

// key = channel name understood by shareInvoice (Invoiceexport.js); perm = switch in access (allowed prop)
const CHANNELS = [
  { key: 'whatsapp', perm: 'whatsapp', label: 'WhatsApp', caption: 'Open chat', Icon: MessageCircle, needsPhone: false },
  { key: 'whatsapp_api', perm: 'whatsappApi', label: 'WhatsApp API', caption: 'Send directly', Icon: Zap, needsPhone: true },
  { key: 'sms', perm: 'sms', label: 'SMS', caption: 'Text message', Icon: MessageSquareText, needsPhone: true },
  { key: 'gmail', perm: 'email', label: 'Email', caption: 'Compose email', Icon: Mail, needsPhone: false },
];

const ALL_ALLOWED = { whatsapp: true, whatsappApi: true, sms: true, email: true };

const API_ORIGIN = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001').replace(/\/api\/v1(?:\/public)?\/?$/, '').replace(/\/+$/, '');
const FEEDBACK_API = `${API_ORIGIN}/api/v1/smiify/sm/feedback`;

const box = { marginTop: 14, padding: 12, border: '1px solid var(--h-color-primary-200, #d9dbff)', borderRadius: 12, background: 'var(--h-color-primary-50, #f5f6ff)' };
const smallBtn = (solid) => ({
  display: 'inline-flex', alignItems: 'center', gap: 5, height: 30, padding: '0 10px', borderRadius: 8, fontFamily: 'inherit', fontSize: 11.5, fontWeight: 850, cursor: 'pointer', textDecoration: 'none', whiteSpace: 'nowrap',
  border: solid ? 0 : '1px solid var(--h-color-primary-200, #d9dbff)',
  background: solid ? 'var(--h-color-primary-600, #673de6)' : '#fff',
  color: solid ? '#fff' : 'var(--h-color-primary-700, #471ea7)',
});

/**
 * Client feedback for this bill: the link (copy / open), send now with the WhatsApp API template,
 * or open the client's chat in WhatsApp with the link typed in.
 * The link appears as soon as the bill is completed.
 */
const FeedbackBox = ({ invoice, allowed, hasPhone }) => {
  const [link, setLink] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState(null);         // { ok, text }
  const [copied, setCopied] = useState(false);
  const completed = invoice?.status === 'completed';
  const id = invoice?._id;
  const storeId = String(invoice?.storeId?._id || invoice?.storeId || '');
  const phone = String(invoice?.customer?.phone || '').replace(/\D/g, '').slice(-10);

  const headers = () => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('smiify_token')}`, ...(storeId ? { 'x-store-id': storeId } : {}) });

  // the link of this bill, made once (the same link every time)
  useEffect(() => {
    if (!completed || !id) return undefined;
    let alive = true;
    setLoading(true);
    fetch(`${FEEDBACK_API}/link`, { method: 'POST', headers: headers(), body: JSON.stringify({ invoiceId: id }) })
      .then((r) => r.json())
      .then((j) => { if (!alive) return; if (j.success && j.data?.url) setLink(j.data.url); else setMsg({ ok: false, text: j.message || 'Could not make the feedback link.' }); })
      .catch(() => alive && setMsg({ ok: false, text: 'Could not make the feedback link.' }))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [completed, id]);

  const copy = async () => {
    try { await navigator.clipboard.writeText(link); } catch (e) { window.prompt('Copy the link', link); }
    setCopied(true); setTimeout(() => setCopied(false), 1800);
  };

  const sendApi = async () => {
    setSending(true); setMsg(null);
    try {
      const res = await fetch(`${FEEDBACK_API}/send`, { method: 'POST', headers: headers(), body: JSON.stringify({ invoiceId: id }) });
      const j = await res.json().catch(() => ({}));
      if (j.data?.url) setLink(j.data.url);
      setMsg({ ok: Boolean(j.success), text: j.message || (res.ok ? 'Sent.' : 'Could not send.') });
    } catch (e) { setMsg({ ok: false, text: 'Cannot reach the server.' }); } finally { setSending(false); }
  };

  const chatText = link ? `Hi ${String(invoice?.customer?.name || '').split(' ')[0] || 'there'}, thank you for visiting us today! 💜 We would love your feedback, it takes just a few seconds: ${link}` : '';

  return (
    <section style={box} aria-label="Client feedback">
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 900, color: 'var(--h-color-neutral-900, #18181a)' }}>
        <Star size={13} color="#ffb020" fill="#ffb020" /> Client feedback
      </div>

      {!completed ? (
        <p style={{ margin: '6px 0 0', fontSize: 11.5, fontWeight: 700, color: 'var(--h-color-neutral-600, #58585e)' }}>Complete the bill to get the feedback link.</p>
      ) : loading && !link ? (
        <p style={{ margin: '8px 0 0', display: 'flex', alignItems: 'center', gap: 6, fontSize: 11.5, fontWeight: 700, color: 'var(--h-color-neutral-600, #58585e)' }}><Loader2 size={13} className={styles.spin} /> Making the link…</p>
      ) : link ? (
        <>
          <div style={{ marginTop: 8, padding: '7px 9px', borderRadius: 8, background: '#fff', border: '1px solid var(--h-color-neutral-150, #e9e9ec)', fontSize: 11.5, fontWeight: 700, color: 'var(--h-color-primary-700, #471ea7)', wordBreak: 'break-all' }}>
            {link}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
            <button type="button" style={smallBtn(false)} onClick={copy}>{copied ? <Check size={12} /> : <Copy size={12} />} {copied ? 'Copied' : 'Copy'}</button>
            <a style={smallBtn(false)} href={link} target="_blank" rel="noreferrer"><ExternalLink size={12} /> Open</a>
            {allowed?.whatsappApi && (
              <button type="button" style={{ ...smallBtn(true), opacity: hasPhone && !sending ? 1 : 0.55, cursor: hasPhone && !sending ? 'pointer' : 'not-allowed' }} onClick={sendApi} disabled={!hasPhone || sending} title={hasPhone ? 'Send the feedback template now' : 'No phone number on this bill'}>
                {sending ? <Loader2 size={12} className={styles.spin} /> : <Zap size={12} />} Send on WhatsApp API
              </button>
            )}
            {allowed?.whatsapp && (
              <a style={smallBtn(false)} href={`https://wa.me/${phone ? `91${phone}` : ''}?text=${encodeURIComponent(chatText)}`} target="_blank" rel="noreferrer">
                <MessageCircle size={12} /> WhatsApp
              </a>
            )}
          </div>
        </>
      ) : null}

      {msg && (
        <div style={{ marginTop: 8, display: 'flex', alignItems: 'flex-start', gap: 6, fontSize: 11.5, fontWeight: 800, color: msg.ok ? 'var(--h-color-success-600, #007a49)' : 'var(--h-color-danger-600, #be1025)' }} role="status">
          {msg.ok ? <CheckCircle2 size={13} /> : <AlertCircle size={13} />} <span>{msg.text}</span>
        </div>
      )}
    </section>
  );
};

/**
 * Share + complete panel of the invoice preview.
 *   allowed      { whatsapp, whatsappApi, sms, email }  which share options this login may use
 *   canComplete  show "Complete bill" on a draft
 * Options without access are not shown at all.
 */
const InvoiceActionPanel = ({
  invoice,
  hasPhone,
  messagePreview,
  shareLink,
  actionLoading,
  error,
  notice,
  onShare,
  onComplete,
  allowed = ALL_ALLOWED,
  canComplete = true,
}) => {
  const isDraft = invoice?.status === 'draft';
  const busy = Boolean(actionLoading);
  const channels = CHANNELS.filter((c) => allowed?.[c.perm] === true);
  const phoneChannelsShown = channels.some((c) => c.needsPhone);

  return (
    <aside className={styles.panel} aria-label="Share invoice">
      <div className={styles.scroll}>
        <h2 className={styles.title}>Share invoice</h2>

        {channels.length === 0 ? (
          <p className={styles.hint} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Lock size={12} /> Sharing is off for your login.
          </p>
        ) : (
          <>
            <div className={styles.grid}>
              {channels.map(({ key, label, caption, Icon, needsPhone }) => {
                const disabled = busy || (needsPhone && !hasPhone);
                const loading = actionLoading === key;
                return (
                  <button
                    key={key}
                    type="button"
                    className={`${styles.tile} ${key === 'whatsapp' ? styles.tilePrimary : ''}`}
                    onClick={() => onShare(key)}
                    disabled={disabled}
                    title={needsPhone && !hasPhone ? 'No phone number on this bill' : label}
                  >
                    <span className={styles.tileIcon}>
                      {loading ? <Loader2 size={18} className={styles.spin} /> : <Icon size={18} />}
                    </span>
                    <span className={styles.tileLabel}>{label}</span>
                    <span className={styles.tileCaption}>{caption}</span>
                  </button>
                );
              })}
            </div>

            {!hasPhone && phoneChannelsShown && <p className={styles.hint}>No phone on this bill. WhatsApp API and SMS need a customer number.</p>}
          </>
        )}

        {(notice || error) && (
          <div className={`${styles.alert} ${error ? styles.alertError : styles.alertOk}`} role="status">
            {error ? <AlertCircle size={14} /> : <CheckCircle2 size={14} />}
            <span>{error || notice}</span>
          </div>
        )}

        {channels.length > 0 && (
          <section className={styles.preview}>
            <span className={styles.previewLabel}>Message preview</span>
            <div className={styles.bubble}>
              <WaText text={messagePreview} />
              {!shareLink && <em className={styles.linkPending}>Invoice link is added when you send.</em>}
            </div>
          </section>
        )}

        {/* client feedback: link, send now, or open the chat */}
        <FeedbackBox invoice={invoice} allowed={allowed} hasPhone={hasPhone} />
      </div>

      {isDraft && canComplete && (
        <div className={styles.footer}>
          <button type="button" className={styles.completeBtn} onClick={onComplete} disabled={busy}>
            {actionLoading === 'complete' ? <Loader2 size={15} className={styles.spin} /> : <Send size={15} />}
            Complete bill
          </button>
        </div>
      )}
    </aside>
  );
};

export default InvoiceActionPanel;